import PropTypes from 'prop-types';

import React, { useEffect, useState } from 'react';

import { Tree, Spin } from 'antd';

import { ItemTypes } from '../../../../constants/constants';

import LocationBarItem from './LocationBarItem';

import './LocationBar.less';

const propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
};
const defaultProps = {
  items: [],
  loading: false,
};

function updateTreeData(list, key, children) {
  return list.map((node) => {
    if (node.key === key) {
      return { ...node, children };
    }

    if (node.children) {
      return { ...node, children: updateTreeData(node.children, key, children) };
    }

    return node;
  });
}

const LocationBar = ({
  rootContainer,
  items,
  currentResource,
  controlConfiguration,
  ownerType,
  permissions,
  loading,
  locationChange,
  onUploadDropedFiles,
  onMoveItems,
  getFolderChildrenForId,
}) => {
  const [treeData, setTreeData] = useState([]);
  const [expandedTreeKeys, setExpandedTreeKeys] = useState([]);
  const [selectedTreeKeys, setSelectedTreeKeys] = useState([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (items && items.length && !mounted) {
      const topLevelItem = {
        title: items[0].name,
        key: items[0].id,
        parent: items[0].parentId ? { key: items[0].parentId } : null,
        isLeaf: false,
        type: ItemTypes.Directory,
        capabilities: items[0].capabilities,
      };

      const expandedKeys = [];
      const selectedKeys = [];

      if (items[0].shouldExpand) {
        expandedKeys.push(topLevelItem.key);
      }

      if (items[0].shouldSelect) {
        selectedKeys.push(topLevelItem.key);
      }

      if (items[0].children && items[0].children.length) {
        trySetChidren(topLevelItem, items[0].children, expandedKeys, selectedKeys);
      }

      const treeItems = [topLevelItem];

      setTreeData(treeItems);
      setExpandedTreeKeys(expandedKeys);
      setSelectedTreeKeys(selectedKeys);

      setMounted(true);
    }
  }, [items]);

  useEffect(() => {
    if (loading) {
      setMounted(false);
    }
  }, [loading]);

  useEffect(() => {
    if (currentResource && currentResource.id && treeData && treeData.length) {
      const expandedPath = findPath(treeData, currentResource.id, []);
      if (expandedPath) setExpandedTreeKeys([...expandedPath]);

      setSelectedTreeKeys([currentResource.id]);
    }
  }, [currentResource, treeData]);

  const findPath = (treeData, key, path = []) => {
    for (let item of treeData) {
      if (item.key === key) {
        return [...path, item.key];
      }
      if (item.children) {
        const foundPath = findPath(item.children, key, [...path, item.key]);
        if (foundPath) {
          return foundPath;
        }
      }
    }
    return null;
  };

  const findItem = (treeData, key) => {
    for (let i = 0; i < treeData.length; i++) {
      if (treeData[i].key === key) {
        return treeData[i];
      }

      if (treeData[i].children) {
        let found = findItem(treeData[i].children, key);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const trySetChidren = (item, children, expandedKeys, selectedKeys) => {
    for (let i = 0; i < children.length; i++) {
      const child = children[i];

      const currentItem = {
        title: child.name,
        key: child.id,
        parent: { key: child.parentId },
        isLeaf: false,
        type: ItemTypes.Directory,
        capabilities: child.capabilities,
        truckingCompanyId: child.truckingCompanyId,
        policyId: child.policyId,
        isPolicyQuote: child.isPolicyQuote,
        taskId: child.taskId,
        systemFolderType: child.systemFolderType,
      };

      if (!item.children) {
        item.children = [];
      }

      if (child.children) {
        trySetChidren(currentItem, child.children, expandedKeys, selectedKeys);
      }

      item.children.push(currentItem);

      if (child.shouldExpand) {
        expandedKeys.push(currentItem.key);
      }

      if (child.shouldSelect) {
        selectedKeys.push(currentItem.key);
      }
    }
  };

  const handleLocationChange = (selectedKeys, selected, resourceId) => {
    if (selected) {
      setSelectedTreeKeys(selectedKeys);
      setMounted(false);
      locationChange(resourceId);
    }
  };

  function onLoadData({ key, children }) {
    return new Promise(async (resolve) => {
      if (children) {
        resolve();
        return;
      }

      const folderChildren = await getFolderChildrenForId(key);

      setTreeData((origin) =>
        updateTreeData(
          origin,
          key,
          folderChildren.map((o) => {
            return {
              title: o.name,
              key: o.id,
              parent: { key: o.parentId },
              isLeaf: false,
              type: ItemTypes.Directory,
              capabilities: o.capabilities,
              truckingCompanyId: o.truckingCompanyId,
              policyId: o.policyId,
              isPolicyQuote: o.isPolicyQuote,
              taskId: o.taskId,
              systemFolderType: o.systemFolderType,
            };
          }),
        ),
      );
      resolve();
    });
  }

  let content = loading ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Spin />
    </div>
  ) : (
    <Tree
      className="oc-fm--location-bar__tree"
      titleRender={(nodeData) => (
        <LocationBarItem
          rootContainer={rootContainer}
          controlConfiguration={controlConfiguration}
          nodeData={nodeData}
          ownerType={ownerType}
          permissions={permissions}
          onMoveItems={onMoveItems}
          onUploadDropedFiles={onUploadDropedFiles}
        />
      )}
      loadData={onLoadData}
      treeData={treeData}
      expandedKeys={expandedTreeKeys}
      selectedKeys={selectedTreeKeys}
      onExpand={(expandedKeys, e) => {
        setExpandedTreeKeys(expandedKeys);
      }}
      onSelect={(selectedKeys, e) => {
        handleLocationChange(selectedKeys, e.selected, e.node.key);
      }}
    />
  );

  return content;
};

LocationBar.propTypes = propTypes;
LocationBar.defaultProps = defaultProps;

export default LocationBar;
